/****************************************************
 * Module Name:  memoModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Container, FormControlLabel, FormGroup, Switch, Tooltip, Stack, } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Modal from "react-bootstrap/Modal";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import MemoProxy from "../../utility/proxy/manager/memoProxy";
import Constants from "../../utility/constants";
import StoneDetailModal from "../common/stoneDetailModal";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import logo from '../../images/pansuriya.jpg'

const memoModal = ({ userId, handleClose, show }) => {

    const [listData, setListData] = useState([]);
    const [mainData, setMainData] = useState([]);

    const [memoLoader, setMemoLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [stoneId, setStoneId] = useState(0);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [groupBy, setGroupBy] = useState(false);
    const [filterValues, setFilterValues] = useState({});

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {
        setMemoLoader(true);
        MemoProxy.FindMemoByUserId(
            userId,
            (response) => {
                if (response) {
                    const uniqueFilterValues = {
                        memodetailstatus: Utility.getDistinctOptions(response, "memodetailstatus"),
                        status: Utility.getDistinctOptions(response, "status"),
                        createdby: Utility.getDistinctOptions(response, "username"),
                        customer: Utility.getDistinctOptions(response, "customerid"),
                    };
                    const combinedFilterValues = {
                        customer: uniqueFilterValues.customer.map(value => {
                            const matchingItem = response.find(item => item.customerid === value);
                            return matchingItem ? `${value} | ${matchingItem.customerusername}` : [];
                        }),
                        memodetailstatus: uniqueFilterValues.memodetailstatus,
                        createdby: uniqueFilterValues.createdby,
                        status: uniqueFilterValues.status,
                    };
                    setFilterValues(combinedFilterValues);
                    setListData(response);
                    handleArray(response);
                    setMemoLoader(false);
                } else {
                    setMemoLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };

    const handleArray = (lListData) => {
        let listIdTracker = 0;
        let newMainData = [];
        if (lListData.length > 0) {
            lListData.map((list) => {
                if (listIdTracker === 0 || list.memoid !== listIdTracker) {
                    listIdTracker = list.memoid;
                    newMainData.push({
                        id: list.memoid,
                        createdat: list.createdat,
                        createdby: list.username,
                        customerusername: list.customerusername,
                        customerid: list.customerid,
                        createdbyid: list.userid,
                        status: list.status,
                        memomasterremark: list.memomasterremark,
                    });
                }
                return true;
            });
        }
        setMainData(newMainData);
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleCloses = () => {
        setShowStoneDetailModal(false);
    };

    const DefaultColumns = [
        {
            headerName: "MEMO ID",
            field: "id",
            defaultSort: "desc",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            headerAlign: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            headerName: "STATUS",
            field: "status",
            align: "center",
            headerAlign: "center",
            width: 200,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            headerName: "CREATED AT",
            field: "createdat",
            width: 200,
            headerAlign: "center",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) =>
                Utility.ConvertUtcToLocalTime(params.value),
            align: "center",
        },
        {
            headerName: "CREATED BY",
            field: "createdby",
            headerAlign: "center",
            align: "center",
            width: 200,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdby : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            headerName: "CUSTOMER",
            field: "customerusername",
            headerAlign: "center",
            width: 200,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.customer : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) =>
                params.row.customerid + " | " + params.row.customerusername,
            valueGetter: (params) =>
                params.row.customerid + " | " + params.row.customerusername,
            align: "center",
        },
        {
            headerName: "USER ID",
            field: "createdbyid",
            align: "center",
            headerAlign: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            headerName: "MEMO REMARK",
            field: "memomasterremark",
            align: "center",
            headerAlign: "center",
            width: 300,
            renderCell: renderCellExpand,
        },
    ];

    const columnName = [
        {
            field: "stoneid",
            headerName: "STONEID",
            width: 100,
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "memodetailstatus",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.memodetailstatus : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "memodetailremark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "lastupdatedat",
            headerName: "LAST UPDATED",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "expectedreceive",
            headerName: "EXPECTED RECEIVE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "updatedat",
            headerName: "UPDATED",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "givenat",
            headerName: "GIVEN",
            headerAlign: "center",
            align: "center",
            type: "dateTime",
            flex: 1,
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
    ];
    const columns = columnName;
    const rows = Utility.GenerateUniqueIdOfResponseForDataGrid(listData);

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback(
        (row, listData) => <DetailPanelContent row={row} listData={listData} />,
        [columns],
    );

    function DetailPanelContent({ row: rowProp, listData }) {
        let row = listData.filter(data => data.id === rowProp.id);
        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "55vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={false}
                        density="compact"
                        columns={columns}
                        rows={row}
                        getRowId={(row) => row.stoneid}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            maxHeight: '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    perfomaid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <div>
            <TabMenuSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleCloses}
                />
            ) : null}
            <Modal show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Memo Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box
                        style={{
                            textAlign: "center",
                            fontSize: "xx-large",
                            marginTop: "0.5rem",
                            width: "100%",
                        }}
                    >
                        Memo Details
                    </Box>
                    <FormGroup sx={{ mt: 1, ml: 4 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={groupBy}
                                    onChange={() => setGroupBy(!groupBy)}
                                />
                            }
                            label="Group By"
                        />
                    </FormGroup>
                    <Container maxWidth="xxl">
                        {!groupBy ? (
                            <div>
                                <Box
                                    sx={{
                                        backgroundColor: '#fff',
                                        color: '#000',
                                        width: "100%",
                                        position: "relative",
                                        "& .MuiTablePagination-selectLabel": {
                                            marginTop: "15px",
                                        },
                                        "& .MuiTablePagination-displayedRows": {
                                            marginTop: "15px",
                                        },
                                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .css-cc8tf1": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .lavenderCell": {
                                            color: "#080808",
                                            backgroundColor: "#D8BFD8",
                                        },
                                        height: "77vh",
                                    }}
                                >
                                    <DataGridPro
                                        disableRowSelectionOnClick={true}
                                        rowHeight={35}
                                        sx={{
                                            '& .MuiDataGrid-columnHeader': {
                                                color: '#fff',
                                                backgroundColor: 'grey',
                                            },
                                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                                py: "4px",
                                            },
                                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                                py: "12px",
                                            },
                                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                                py: "18px",
                                            },

                                            '& .MuiDataGrid-row': {
                                                whiteSpace: 'nowrap',
                                            },
                                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                        }}
                                        loading={memoLoader}
                                        slots={{ toolbar: GridToolbar }}
                                        rows={rows}
                                        columns={columns}
                                        getRowId={(row) => row.uniqueId}
                                        initialState={{
                                            columns: {
                                                columnVisibilityModel: {
                                                    // Hide columns id, the other columns will remain visible
                                                    labname: false,
                                                    location: false,
                                                    country: false,
                                                },
                                            },
                                            pagination: {
                                                paginationModel: Constants.pageSize,
                                            },
                                        }}
                                        pageSizeOptions={Constants.pageSizeOptions}
                                        pagination
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: true,
                                                quickFilterProps: { debounceMs: 500 },
                                            },
                                        }}
                                    />
                                    <Box
                                        style={{
                                            position: "absolute",
                                            top: 5,
                                            right: "270px",
                                            display: "flex",
                                            gap: "15px",
                                        }}
                                    >
                                        {memoLoader ? (
                                            <CircularProgress size="1rem" />
                                        ) : (
                                            <Tooltip title="Reload List">
                                                <RefreshOutlinedIcon
                                                    onClick={() =>
                                                        handleSearch()
                                                    }
                                                    color="primary"
                                                    fontSize="large"
                                                    sx={{ cursor: "pointer" }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </div>
                        ) : (
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "100%",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .lavenderCell": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                    },
                                    height: "77vh",
                                }}
                            >
                                <DataGridPro
                                    rowHeight={65}
                                    disableRowSelectionOnClick={false}
                                    density="compact"
                                    columns={DefaultColumns}
                                    rows={mainData}
                                    loading={memoLoader}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "4px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "12px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "18px",
                                        },

                                        '& .MuiDataGrid-row': {
                                            whiteSpace: 'nowrap',
                                        },
                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    slots={{ toolbar: GridToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                    getRowId={(row) => row.id}
                                    initialState={{
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                    }}
                                    sortModel={[
                                        {
                                            field: 'id',
                                            sort: 'desc',
                                        },
                                    ]}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={({ row }) => getDetailPanelContent(row, listData)}
                                />
                                <Box
                                    style={{
                                        position: "absolute",
                                        top: 2,
                                        right: "270px",
                                        display: "flex",
                                        flexWrap: "nowrap",
                                    }}
                                >
                                    {memoLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() => handleSearch()}
                                                color="primary"
                                                fontSize="large"
                                                sx={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        )
                        }
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default memoModal;
